import { UPTIME_CAUSE } from '../stores/activity';
import { STORE_CATEGORY, STORE_STATUS } from '../stores/store';

export interface YandexTwoGisMapData {
  mapId: string;
  center_x: number;
  center_y: number;
  label: string;
  map_name?: string;
  max_zoom: number;
  min_zoom: number;
  placemark_x: number;
  placemark_y: number;
  type?: string;
  map_as_string?: string;
  zoom: number;
}

export interface TwoGisInputMap {
  zoom?: number | string;
  markX?: number;
  markY?: number;
  cameraPositionX?: number;
  cameraPositionY?: number;
}

interface PosResponse {
  accountId: number;
  appVersion: string;
  deleted: boolean;
  id: number;
  name: string;
  storeId: number;
  toBeDeleted: boolean;
  virtual: boolean;
  key: string;
}

interface PosConfResponse {
  lastCreatedAgent?: any;
  lastSyncTime?: number;
  posId: number;
  printerFooter: string;
  printerHeader: string;
  printerLanguage: number;
  printerPaperWidth: number;
  printerTableView: boolean;
  printerVatOnReceipt: boolean;
}

interface WebkassaResponse {
  active: boolean;
  iinBin: string;
  posFactoryNo: string;
  posId: number;
  taxpayer: boolean;
  taxpayerName: string;
  taxpayerVATNo: string;
  taxpayerVATSerialNo: string;
  wkAccountId: number;
  errorString: string;
  lastErrorTime: number;
}

export interface KassaResponse {
  pos?: PosResponse;
  posConfig?: PosConfResponse;
  webkassaPos?: WebkassaResponse;
}

export interface WebkassaLogin {
  companyId: any;
  login: string;
  password: string;
  kassaTypeId: string;
}

export type MapValueSetter = (data: TwoGisInputMap) => void;

export enum GeoUnitType {
  COUNTRY = 'COUNTRY',
  REGION = 'REGION',
  SETTLEMENT = 'SETTLEMENT',
}

export const roundOption = [
  { value: 'NOT_ROUND', name: 'PARTNERS.settings.do-not-round' },
  { value: 'ROUND_UP_TO_ONE', name: 'PARTNERS.settings.1tg-up' },
  { value: 'ROUND_DOWN_TO_ONE', name: 'PARTNERS.settings.1tg-down' },
  { value: 'ROUND_UP_TO_FIVE', name: 'PARTNERS.settings.5tg-up' },
  { value: 'ROUND_DOWN_TO_FIVE', name: 'PARTNERS.settings.5tg-down' },
  { value: 'ROUND_UP_TO_TEN', name: 'PARTNERS.settings.10tg-up' },
  { value: 'ROUND_DOWN_TO_TEN', name: 'PARTNERS.settings.10tg-down' },
];

export const userOption = [
  { value: 'NO_ONE', name: 'PARTNERS.settings.no-one' },
  { value: 'ADMINISTRATOR', name: 'PARTNERS.settings.admin' },
  { value: 'ALL', name: 'PARTNERS.settings.all' },
];

export const storeConfigKeysFields = {
  PRODUCT_EDITABLE: [false],
  PRICE_EDITABLE: [false],
  MORE_THAN_MILLION: [false],
  UNIVERSAL_PRODUCT: [false],
  MANAGE_VIA_INVOICE_PRICE: [false],
  CANCEL_PAYMENT: [false],
  GLOBAL_PRODUCT_SEARCH_ON_POS: [false],
  INTEGRATION_1C: [false],
  KASSA_WHOLESALE: [false],
  WHOLESALE_PRODUCT: [false],
  PACKAGE_SELLING_PRICE_EDITABLE: [false],
  MINIMIZABLE: [false],
  DISCOUNT: [false],
  DEBT: [false],
  HIDE_STOCK_IN_REVISION: [false],
  BANK_TRANSFER: [false],
  CASH_IN_OUT: [false],
  DEFER_SALE: [false],
  SELLING_PRICE_EDITABLE: [false],
  ORDER: [false],
  CAFE_ENABLED: [false],
};

export const storeConfigFields = {
  allowedToRefundSale: ['NO_ONE'],
  scheduledMarkupFromTime: [null],
  scheduledMarkupToTime: [null],
  notPrintCheckFromTime: [null],
  notPrintCheckToTime: [null],
  allowedToRefundSaleWithoutCheck: ['NO_ONE'],
  allowedToCancelProduct: ['NO_ONE'],
  allowedToDecreaseProductQuantity: ['NO_ONE'],
  weightProductRoundOption: ['NOT_ROUND'],
  sellingPriceRoundOption: ['NOT_ROUND'],
  discountRoundOption: ['NOT_ROUND'],
};

export interface User {
  cashierPin: string;
  companyId: number;
  deleted: boolean;
  email: string;
  firstName: string;
  id: number;
  lastName: number;
  passwordUpdateTime: any;
  permissionUpdateTime: number;
  phoneNumber: string;
  toBeDeleted: boolean;
  userGroupId: number;
  updateTime: Date | number;
  createTime: Date | number;
  stores?: any[];
}

export interface Store {
  name: string;
  storeGroupId: number;
}

export interface ExtendedStore extends Store {
  companyId: number;
  createTime: number;
  deleted: false;
  id: number;
  toBeDeleted: boolean;
}

export interface TwoGisBackendData {
  map_name: string;
  placemark_x: number;
  placemark_y: number;
  zoom: number;
  min_zoom: number;
  max_zoom: number;
  center_x: number;
  center_y: number;
  label: string;
  type: string;
}

export interface StoreDetail {
  geoUnitId: number;
  type: STORE_CATEGORY;
  classification: string;
  area: number;
  oprByDay: string;
  map: string;
  address: string;
  postcode: string;
  contactUserId: number;
  workSchedule: string;
  note: string;
  configurableInput?: string;
  status: STORE_STATUS;
  cause: UPTIME_CAUSE;
  uptime: number;
}

export interface CreateStore {
  store: Store;
  storeDetail: StoreDetail;
  marginType: number;
  copy: boolean;
  storeIdCopyFrom?: number;
  supporters: number[];
}

export const companyTypes = [
  { name: 'ИП', id: 'ИП' },
  { name: 'ТОО', id: 'ТОО' },
  { name: 'ФЛ', id: 'ФЛ' },
  { name: 'АО', id: 'АО' },
  { name: 'РГКП', id: 'РГКП' },
  { name: 'РГП', id: 'РГП' },
  { name: 'ОО', id: 'ОО' },
  { name: 'Другое', id: 'Другое' },
];

export interface UserListResponse {
  count: number;
  userGroups: UserGroupRoles[];
  users: User[];
}

export interface UserGroupRoles {
  companyId: null;
  deleted: boolean;
  id: number;
  name: string;
  systemCode: null;
}

export const priceCodeCopyProgressText = {
  price: {
    checking_text: 'Идет проверка на идентичность цен',
    check_success_text: 'Цены идентичные',
    check_copy_text:
      'В выбранной группе цены не одинаковые.|' +
      'Пожалуйста выберите магазин с которого будут скопированы данные в другие магазины в группе.',
    copying_text: 'Идет копирование цен',
    copy_success_text: 'копирование прошло успешно!',
  },
  code: {
    checking_text: 'Идет проверка на идентичность доп.кодов',
    check_success_text: 'Доп.кода идентичные',
    check_copy_text:
      'В выбранной группе доп.кода не одинаковые.|' +
      'Пожалуйста выберите магазин с которого будут скопированы данные в другие магазины в группе.',
    copying_text: 'Идет копирование доп.кодов',
    copy_success_text: 'копирование прошло успешно!',
  },
};

export enum Partners {
  MASTERFRANCHISE = 'common.MASTERFRANCHISE',
  FRANCHISE = 'common.FRANCHISE',
  COMPANY = 'common.COMPANY',
  STORE = 'common.STORE',
}

export enum CompanyLevels {
  START = 'START',
  SILVER = 'SILVER',
  GOLD = 'GOLD',
  DIAMOND = 'DIAMOND',
  BRONZE = 'BRONZE',
}

export const companyLevels = Object.keys(CompanyLevels);
